import React, { useState, useEffect, useRef, useCallback } from 'react';
import Webcam from './components/Webcam';
import Stats from './components/Stats';
import Transcript from './components/Transcript';
import FinalScore from './components/FinalScore';
import CoachingPanel from './components/CoachingPanel';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:6767';

const DEFAULT_QUESTIONS = [
  "Tell me about yourself.",
  "What is your greatest professional strength?",
  "Describe a challenge you overcame at work.",
  "Why do you want to work here?",
  "Where do you see yourself in 5 years?",
  "What's your biggest weakness?",
  "Tell me about a time you led a team.",
  "How do you handle pressure or tight deadlines?",
  "What motivates you at work?",
  "Do you have any questions for us?",
];

const SPEECH_SUPPORTED = 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;

const FILLER_RE = /\b(you know|i mean|sort of|kind of|uh-huh|mm-hmm|um|uh|hmm|mm|mhm|er|ah|like|basically|actually|literally|yeah|so|well|okay|right|gonna|wanna|gotta)\b/gi;

function scoreAnswer(text, avgStats) {
  const words = text.trim().split(/\s+/).filter(Boolean);
  const n = words.length;
  if (n === 0) return 0;

  let lengthScore;
  if (n < 15)        lengthScore = Math.round((n / 15) * 40);
  else if (n <= 180) lengthScore = Math.min(100, 40 + Math.round(((n - 15) / 165) * 60));
  else               lengthScore = Math.max(30, 100 - Math.round(((n - 180) / 150) * 70));

  const l = text.toLowerCase();
  const hasContext = /\b(when|while|during|in my|at my|we had|there was|the (project|team|situation)|previously|last (year|job|role)|at the time)\b/.test(l);
  const hasAction  = /\bi (led|built|created|implemented|developed|decided|took|helped|managed|designed|wrote|fixed|solved|set up|introduced|started|organized|reached|worked on)\b/.test(l);
  const hasResult  = /\b(result|outcome|as a result|led to|ended up|successfully|improved|reduced|increased|learned|ultimately|achieved|delivered|saved|grew|gained|went from)\b/.test(l);
  const structScore = Math.round(([hasContext, hasAction, hasResult].filter(Boolean).length / 3) * 100);

  const clarityScore = Math.max(0, 100 - (avgStats?.fillerWords ?? 0) * 4);

  return Math.round(lengthScore * 0.35 + structScore * 0.45 + clarityScore * 0.20);
}

function App() {
  const [isRunning, setIsRunning] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [stats, setStats] = useState({
    emotion: 'neutral', confidence: 0, eyeContact: 85, wpm: 120, nervousness: 20, fillerWords: 5,
  });
  const [issues, setIssues] = useState({});
  const [interruption, setInterruption] = useState(null);
  const [emotionHistory, setEmotionHistory] = useState([]);
  const [transcript, setTranscript] = useState([]);
  const [interimText, setInterimText] = useState('');
  const [liveWpm, setLiveWpm] = useState(0);
  const [srListening, setSrListening] = useState(false);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [statsHistory, setStatsHistory] = useState([]);
  const [mediaStream, setMediaStream] = useState(null);
  const [questionBreakdowns, setQuestionBreakdowns] = useState([]);
  const [sessionHistory, setSessionHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ic-session-history') || '[]'); }
    catch { return []; }
  });

  const clearHistory = () => {
    localStorage.removeItem('ic-session-history');
    setSessionHistory([]);
  };

  const recognitionRef        = useRef(null);
  const isRunningRef          = useRef(false);
  const statsRef              = useRef(stats);
  const speechStartRef        = useRef(null);
  const lastPingRef           = useRef(0);
  const transcriptRef         = useRef([]);
  const questionsRef          = useRef([]);
  const qStatsBufferRef       = useRef([]);
  const qTranscriptStartRef   = useRef(0);
  const prevQuestionIdxRef    = useRef(0);
  // Accumulated filler counts from every finalized chunk — not truncated like transcript state
  const sessionFillerRef      = useRef({ words: 0, fillers: 0 });

  // Devices
  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedAudioId, setSelectedAudioId] = useState('');
  const [selectedVideoId, setSelectedVideoId] = useState('');

  // Questions
  const [questions, setQuestions] = useState(() => {
    try {
      const saved = localStorage.getItem('ic-questions');
      return saved ? JSON.parse(saved) : DEFAULT_QUESTIONS;
    } catch { return DEFAULT_QUESTIONS; }
  });
  const [newQuestion, setNewQuestion] = useState('');

  useEffect(() => { localStorage.setItem('ic-questions', JSON.stringify(questions)); }, [questions]);
  useEffect(() => { isRunningRef.current = isRunning; }, [isRunning]);
  useEffect(() => { statsRef.current = stats; }, [stats]);
  useEffect(() => { transcriptRef.current = transcript; }, [transcript]);
  useEffect(() => { questionsRef.current = questions; }, [questions]);

  // Clean up speech recognition if the component unmounts while a session is active
  useEffect(() => {
    return () => { stopSpeechRecognition(); };
  }, []);

  // Snapshot the current question's stats + transcript into questionBreakdowns
  const saveCurrentQuestionBreakdown = useCallback(() => {
    const buffer = qStatsBufferRef.current;
    if (buffer.length === 0) return;
    const qIdx  = prevQuestionIdxRef.current;
    const qs    = questionsRef.current;
    const activeQs = qs.length > 0 ? qs : DEFAULT_QUESTIONS;
    const avgField = (key) => Math.round(buffer.reduce((s, x) => s + (x[key] || 0), 0) / buffer.length);
    const answerLines = transcriptRef.current.slice(qTranscriptStartRef.current);
    const answerText  = answerLines.map(l => l.text).join(' ');
    const _aWords   = answerText.trim().split(/\s+/).filter(Boolean).length;
    const _aFillers = (answerText.match(FILLER_RE) || []).length;
    const avgStats = {
      eyeContact:  avgField('eyeContact'),
      nervousness: avgField('nervousness'),
      fillerWords: _aWords >= 5 ? Math.round((_aFillers / _aWords) * 100) : avgField('fillerWords'),
      wpm:         avgField('wpm'),
    };
    setQuestionBreakdowns(prev => [...prev, {
      questionIdx: qIdx,
      question:    activeQs[qIdx] || `Question ${qIdx + 1}`,
      stats:       avgStats,
      answerText,
      wordCount:   answerText.trim().split(/\s+/).filter(Boolean).length,
      answerScore: scoreAnswer(answerText, avgStats),
    }]);
  }, []);

  // Accumulate per-question stats samples (every 2 s, same cadence as statsHistory)
  useEffect(() => {
    if (!isRunning) return;
    const t = setInterval(() => { qStatsBufferRef.current.push({ ...statsRef.current }); }, 2000);
    return () => clearInterval(t);
  }, [isRunning]);

  // When the user navigates to a different question, snapshot the previous one
  useEffect(() => {
    if (!isRunning) return;
    if (questionIdx === prevQuestionIdxRef.current) return;
    saveCurrentQuestionBreakdown();
    prevQuestionIdxRef.current    = questionIdx;
    qStatsBufferRef.current       = [];
    qTranscriptStartRef.current   = transcriptRef.current.length;
  }, [questionIdx, isRunning, saveCurrentQuestionBreakdown]);

  // Receive full frame analysis payload from Webcam and fan out to each state slice
  const handleStatsUpdate = useCallback((payload) => {
    const { issues: newIssues, interruption: newInterruption, confidence, ...coreStats } = payload;
    setStats(prev => ({ ...prev, ...coreStats, confidence: confidence ?? 0 }));
    if (newIssues) setIssues(newIssues);
    setInterruption(newInterruption?.active ? newInterruption : null);
    if (coreStats.emotion) {
      setEmotionHistory(prev => [...prev.slice(-19), { emotion: coreStats.emotion, confidence: confidence ?? 0 }]);
    }
  }, []);

  const refreshDevices = async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setAudioDevices(all.filter(d => d.kind === 'audioinput'));
      setVideoDevices(all.filter(d => d.kind === 'videoinput'));
    } catch {}
  };

  useEffect(() => {
    refreshDevices();
    navigator.mediaDevices.addEventListener('devicechange', refreshDevices);
    return () => navigator.mediaDevices.removeEventListener('devicechange', refreshDevices);
  }, []);

  // Timer
  useEffect(() => {
    if (!isRunning) return;
    const t = setInterval(() => setElapsedTime(p => p + 1), 1000);
    return () => clearInterval(t);
  }, [isRunning]);

  // Stats history for final score averaging
  useEffect(() => {
    if (!isRunning) return;
    const t = setInterval(() => setStatsHistory(h => [...h.slice(-120), { ...statsRef.current }]), 2000);
    return () => clearInterval(t);
  }, [isRunning]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const avg = (arr, key) => arr.length
    ? Math.round(arr.reduce((s, x) => s + x[key], 0) / arr.length) : null;

  const startSpeechRecognition = () => {
    if (!SPEECH_SUPPORTED) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.continuous     = true;
    recognition.interimResults = true;
    recognition.lang           = 'en-US';

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          const finalText = text.trim();
          if (!finalText) continue;
          const duration = speechStartRef.current
            ? Math.max(0.5, (Date.now() - speechStartRef.current) / 1000)
            : 2.0;
          speechStartRef.current = null;
          setLiveWpm(0);
          setInterimText('');
          setTranscript(prev => [...prev.slice(-14), { text: finalText }]);
          // Accumulate into session totals (untruncated — used for final filler score)
          const _fw = finalText.split(/\s+/).filter(Boolean).length;
          const _ff = (finalText.match(FILLER_RE) || []).length;
          sessionFillerRef.current.words   += _fw;
          sessionFillerRef.current.fillers += _ff;
          fetch(`${BACKEND_URL}/analyze-text`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: finalText, duration }),
          }).catch(() => {});
        } else {
          interim += text;
          const now = Date.now();
          if (!speechStartRef.current) speechStartRef.current = now;

          // Compute live WPM word-by-word from interim text
          const wordCount = interim.trim().split(/\s+/).filter(Boolean).length;
          const elapsed   = (now - speechStartRef.current) / 1000;
          if (elapsed >= 0.5 && wordCount >= 2) {
            setLiveWpm(Math.min(300, Math.round((wordCount / elapsed) * 60)));
          }

          // Debounced ping to keep backend silence timer alive (max once per 400ms)
          if (now - lastPingRef.current >= 400) {
            lastPingRef.current = now;
            fetch(`${BACKEND_URL}/speech-ping`, { method: 'POST' }).catch(() => {});
          }
        }
      }
      if (interim) setInterimText(interim);
    };

    recognition.onspeechstart = () => setSrListening(true);
    recognition.onspeechend   = () => setSrListening(false);

    recognition.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        recognition.onend = null; // stop restart loop
        setSrListening(false);
        alert('Microphone access was denied. Please allow mic permissions and refresh.');
      } else if (e.error !== 'no-speech' && e.error !== 'aborted') {
        console.warn('SR error:', e.error);
      }
    };

    recognition.onend = () => {
      if (!isRunningRef.current) return;
      try { recognition.start(); } catch (err) {
        console.warn('SR restart failed:', err);
      }
    };

    try {
      recognition.start();
    } catch (err) {
      console.error('SR failed to start:', err);
    }
    recognitionRef.current = recognition;
  };

  const stopSpeechRecognition = () => {
    if (recognitionRef.current) {
      recognitionRef.current.onend    = null;
      recognitionRef.current.onerror  = null;
      recognitionRef.current.onresult = null;
      try { recognitionRef.current.stop(); } catch {}
      recognitionRef.current = null;
    }
    setInterimText('');
    setLiveWpm(0);
    setSrListening(false);
    speechStartRef.current = null;
    lastPingRef.current    = 0;
  };

  const handleStart = async () => {
    try {
      const videoConstraint = selectedVideoId ? { deviceId: { exact: selectedVideoId } } : true;

      // Request camera + mic together so both permissions are granted before SR starts
      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraint,
        audio: true,
      });

      // Stop audio tracks immediately — SR handles its own mic access,
      // but we need this getUserMedia call to pre-grant the permission
      stream.getAudioTracks().forEach(t => t.stop());

      setMediaStream(stream);
      await refreshDevices();
      await fetch(`${BACKEND_URL}/start`, { method: 'POST' });
      setIsRunning(true);
      setShowResults(false);
      setTranscript([]);
      setElapsedTime(0);
      setStatsHistory([]);
      setQuestionIdx(0);
      setIssues({});
      setInterruption(null);
      setEmotionHistory([]);
      setLiveWpm(0);
      setQuestionBreakdowns([]);
      qStatsBufferRef.current       = [];
      qTranscriptStartRef.current   = 0;
      prevQuestionIdxRef.current    = 0;
      sessionFillerRef.current      = { words: 0, fillers: 0 };
      startSpeechRecognition();
    } catch (err) {
      // Clean up any stream that was opened before the error
      try { stream?.getTracks().forEach(t => t.stop()); } catch {}
      if (err.name === 'NotAllowedError') {
        alert('Camera and microphone access are required. Please allow both permissions and try again.');
      } else {
        alert('Failed to start — is the backend running?');
      }
    }
  };

  const handleStop = async () => {
    saveCurrentQuestionBreakdown();
    stopSpeechRecognition();
    if (mediaStream) { mediaStream.getTracks().forEach(t => t.stop()); setMediaStream(null); }
    try { await fetch(`${BACKEND_URL}/stop`, { method: 'POST' }); } catch {}
    setIsRunning(false);
    setShowResults(true);
  };

  const addQuestion = () => {
    const q = newQuestion.trim();
    if (!q) return;
    setQuestions(qs => [...qs, q]);
    setNewQuestion('');
  };
  const deleteQuestion = (i) => setQuestions(qs => qs.filter((_, idx) => idx !== i));
  const moveQuestion = (i, dir) => {
    setQuestions(qs => {
      const next = [...qs];
      const j = i + dir;
      if (j < 0 || j >= next.length) return qs;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  // Filler rate from untruncated session accumulator (every finalized chunk, not just last 14)
  const { words: _sfWords, fillers: _sfFillers } = sessionFillerRef.current;
  const frontendFillerPct = _sfWords >= 10 ? Math.round((_sfFillers / _sfWords) * 100) : null;

  const finalStats = statsHistory.length > 5 ? {
    emotion: stats.emotion,
    eyeContact:  avg(statsHistory, 'eyeContact')  ?? stats.eyeContact,
    wpm:         avg(statsHistory, 'wpm')          ?? stats.wpm,
    nervousness: avg(statsHistory, 'nervousness')  ?? stats.nervousness,
    fillerWords: frontendFillerPct ?? avg(statsHistory, 'fillerWords') ?? stats.fillerWords,
  } : { ...stats, fillerWords: frontendFillerPct ?? stats.fillerWords };

  // Live filler rate from all committed transcript lines + current interim text
  const allText       = [...transcript.map(l => l.text), interimText].join(' ');
  const allWords      = allText.trim().split(/\s+/).filter(Boolean).length;
  const allFillers    = (allText.match(FILLER_RE) || []).length;
  const liveFillerPct = allWords >= 5 ? Math.round((allFillers / allWords) * 100) : -1;

  if (showResults) {
    return <FinalScore stats={finalStats} elapsedTime={elapsedTime} onRestart={() => {
      setShowResults(false);
      try { setSessionHistory(JSON.parse(localStorage.getItem('ic-session-history') || '[]')); } catch {}
    }} questionBreakdowns={questionBreakdowns} />;
  }

  const activeQuestions = questions.length > 0 ? questions : DEFAULT_QUESTIONS;

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <h1>interview coach</h1>
          <p>AI-powered real-time practice</p>
        </div>
        {isRunning && (
          <div className="session-badge">
            <span className="rec-dot" />
            <span className="session-time">{formatTime(elapsedTime)}</span>
            {SPEECH_SUPPORTED && (
              <span className="mic-status" title={srListening ? 'Mic active' : 'Mic idle'}>
                {srListening ? '🎤' : '🔇'}
              </span>
            )}
          </div>
        )}
      </header>

      {!SPEECH_SUPPORTED && (
        <div className="browser-notice">
          <span className="browser-notice-icon">⚠️</span>
          <span>
            Speech recognition requires <strong>Chrome</strong> or <strong>Edge</strong>.
            Transcript and WPM stats won't be available in this browser.
          </span>
        </div>
      )}

      {isRunning && (
        <div className="question-bar">
          <span className="question-counter">{questionIdx + 1}/{activeQuestions.length}</span>
          <p className="question-text">"{activeQuestions[questionIdx]}"</p>
          <div className="question-nav">
            <button className="q-btn" onClick={() => setQuestionIdx(i => Math.max(0, i - 1))} disabled={questionIdx === 0}>‹</button>
            <button className="q-btn" onClick={() => setQuestionIdx(i => Math.min(activeQuestions.length - 1, i + 1))} disabled={questionIdx === activeQuestions.length - 1}>›</button>
          </div>
        </div>
      )}

      {!isRunning && (
        <div className="setup-panel">
          <div className="setup-card">
            <h3 className="setup-title">input devices</h3>
            <div className="device-row">
              <div className="device-field">
                <label>camera</label>
                <select value={selectedVideoId} onChange={e => setSelectedVideoId(e.target.value)}>
                  <option value="">default camera</option>
                  {videoDevices.map(d => (
                    <option key={d.deviceId} value={d.deviceId}>{d.label || `camera ${d.deviceId.slice(0, 8)}`}</option>
                  ))}
                </select>
              </div>
              <div className="device-field">
                <label>microphone</label>
                <select value={selectedAudioId} onChange={e => setSelectedAudioId(e.target.value)} disabled={!SPEECH_SUPPORTED}>
                  <option value="">default microphone</option>
                  {audioDevices.map(d => (
                    <option key={d.deviceId} value={d.deviceId}>{d.label || `microphone ${d.deviceId.slice(0, 8)}`}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="setup-hint">device names appear after first permission grant</p>
          </div>

          <div className="setup-card">
            <div className="setup-title-row">
              <h3 className="setup-title">interview questions</h3>
              <button className="btn-reset" onClick={() => setQuestions(DEFAULT_QUESTIONS)}>reset to defaults</button>
            </div>
            <div className="question-list">
              {questions.map((q, i) => (
                <div key={`${i}-${q}`} className="question-list-item">
                  <span className="q-num">{i + 1}</span>
                  <span className="q-text">{q}</span>
                  <div className="q-actions">
                    <button className="q-action-btn" onClick={() => moveQuestion(i, -1)} disabled={i === 0} title="move up">↑</button>
                    <button className="q-action-btn" onClick={() => moveQuestion(i, 1)} disabled={i === questions.length - 1} title="move down">↓</button>
                    <button className="q-action-btn q-delete" onClick={() => deleteQuestion(i)} title="delete">×</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="add-question-row">
              <input
                className="add-question-input"
                type="text"
                placeholder="add a new question..."
                value={newQuestion}
                onChange={e => setNewQuestion(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addQuestion()}
              />
              <button className="btn-add-q" onClick={addQuestion}>add</button>
            </div>
          </div>
        </div>
      )}

      {!isRunning && sessionHistory.length > 0 && (() => {
        const recent = [...sessionHistory].reverse().slice(0, 5);
        return (
          <div className="history-panel">
            <div className="history-panel-header">
              <span className="history-panel-title">recent sessions</span>
              <div className="history-sparkline" title="score trend (oldest → newest)">
                {sessionHistory.slice(-8).map((s, i) => {
                  const c = s.overall >= 75 ? '#4ade80' : s.overall >= 55 ? '#fbbf24' : '#ef4444';
                  return <span key={i} className="spark-pip" style={{ background: c }} title={`${s.overall} (${s.grade})`} />;
                })}
              </div>
              <button className="btn-clear-history" onClick={clearHistory}>clear</button>
            </div>
            <div className="history-list">
              {recent.map((s, i) => {
                const older = recent[i + 1];
                const delta = older ? s.overall - older.overall : null;
                const trendIcon  = delta === null ? null : delta > 0 ? '↑' : delta < 0 ? '↓' : '→';
                const trendColor = delta > 0 ? '#4ade80' : delta < 0 ? '#ef4444' : '#555';
                const scoreColor = s.overall >= 75 ? '#4ade80' : s.overall >= 55 ? '#fbbf24' : '#ef4444';
                const d = new Date(s.date);
                const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                const m = Math.floor(s.duration / 60), sec = s.duration % 60;
                return (
                  <div key={i} className="history-row">
                    <div className="history-score-col">
                      <span className="history-score-num" style={{ color: scoreColor }}>{s.overall}</span>
                      <span className="history-grade-badge" style={{ color: scoreColor }}>{s.grade}</span>
                    </div>
                    <div className="history-info-col">
                      <span className="history-date">{dateStr} · {timeStr}</span>
                      <span className="history-dur">{m > 0 ? `${m}m ${sec}s` : `${sec}s`}</span>
                    </div>
                    <div className="history-metrics-col">
                      <span style={{ color: s.eyeContact >= 70 ? '#4ade80' : '#fbbf24' }}>👁 {s.eyeContact}%</span>
                      <span style={{ color: s.wpm >= 100 && s.wpm <= 160 ? '#4ade80' : s.wpm > 0 ? '#fbbf24' : '#555' }}>🎙 {s.wpm > 0 ? `${s.wpm}` : '—'}</span>
                      <span style={{ color: s.composure >= 75 ? '#4ade80' : '#fbbf24' }}>🧘 {s.composure}%</span>
                    </div>
                    {trendIcon && <span className="history-trend" style={{ color: trendColor }}>{trendIcon}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      <main className="app-main">
        <div className="left-panel">
          <Webcam isRunning={isRunning} stream={mediaStream} onStatsUpdate={handleStatsUpdate} backendUrl={BACKEND_URL} />
          <div className="controls">
            {!isRunning ? (
              <button className="btn-start" onClick={handleStart}>start interview</button>
            ) : (
              <button className="btn-stop" onClick={handleStop}>end &amp; get score</button>
            )}
          </div>
        </div>

        <div className="right-panel">
          <Stats stats={stats} isRunning={isRunning} emotionHistory={emotionHistory} liveWpm={liveWpm} liveFillerPct={liveFillerPct} />
          <CoachingPanel issues={issues} interruption={interruption} isRunning={isRunning} stats={stats} liveWpm={liveWpm} liveFillerPct={liveFillerPct} />
          <Transcript lines={transcript} interimText={interimText} />
        </div>
      </main>
    </div>
  );
}

export default App;
